/**
 * FunChallengesGlobalListener
 *
 * Global mirror of the chess `ChallengeComponent` for the Fun Challenges
 * feature. Mounted once near the top of the app tree (next to
 * `<ChallengeComponent>`) so that:
 *
 *   - `useFunChallengesSocket` registers the fun_* handlers exactly once,
 *     regardless of which screen the user is on.
 *   - Incoming challenges (`fun_room_invite`) interrupt every screen with a
 *     unified accept/decline modal.
 *   - The "Waiting for opponent…" state survives navigation between the
 *     lobby and other screens.
 *   - Server-pushed `fun_session_start` causes us to navigate to the battle
 *     route automatically.
 *
 * One `<SlideModal>` whose body branches on `funChallengesBattleStore.status`.
 * Mirrors the architecture of `src/Components/ChallengeComponent/index.js`.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import {useNavigation} from '@react-navigation/native';

import Box from '@/Atoms/Box';
import CCButton from '@/Atoms/CCButton';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import SlideModal from '@/Atoms/SlideModal';
import {FontSizes, vs} from '@/Utils/Dimensions';

import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesSessionStore from '@/Store/funChallengesSessionStore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import useFunChallengesBattleSocket from '@/Hooks/FunChallengesSocket/useFunChallengesBattleSocket';
import {useFunChallengesSocket} from '@/Hooks/FunChallengesSocket/useFunChallengesSocket';
import {BattleType} from '@/Screens/FunChallenges/types';
import useGamePlayState from '@/Store/GamePlayStore';
import ChallengeRejected from '@/Screens/PlayChess/components/ChallengeRejected';

// Server's invite timer is 30 s. We auto-decline a bit earlier to leave room
// for network jitter so the reject travels before the server times out the
// room itself (which would surface a less specific INVITE_EXPIRED to both
// sides). 20 s is the balance picked with the user.
const INCOMING_AUTO_DECLINE_MS = 20_000;
const ERROR_AUTO_CLEAR_MS = 4_000;

export default function FunChallengesGlobalListener() {
  // Touch the socket hook once — this triggers the fun_* schema/handler
  // registrations regardless of whether the user has visited the lobby yet.
  useFunChallengesSocket();

  const navigation = useNavigation<any>();

  const status = useFunChallengesBattleStore(s => s.status);
  const incomingChallenge = useFunChallengesBattleStore(s => s.incomingChallenge);
  const pendingChallenge = useFunChallengesBattleStore(s => s.pendingChallenge);
  const errorMessage = useFunChallengesBattleStore(s => s.errorMessage);
  const session = useFunChallengesBattleStore(s => s.session);
  const clearError = useFunChallengesBattleStore(s => s.clearError);

  const {acceptChallenge, rejectChallenge, cancelChallenge, startGame} =
    useFunChallengesBattleSocket();

  // Room mirror — used by the auto-start effect below.
  const roomMirrorId = useFunChallengesSessionStore(s => s.room?.roomId);
  const roomPlayersCount = useFunChallengesSessionStore(
    s => s.room?.players?.length ?? 0,
  );
  const roomState = useFunChallengesSessionStore(s => s.room?.state);

  const startedRoomRef = useRef<string | null>(null);

  const [autoDeclineSecondsLeft, setAutoDeclineSecondsLeft] = useState(
    INCOMING_AUTO_DECLINE_MS / 1000,
  );
  const incomingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Auto-decline countdown ───────────────────────────────────────────
  useEffect(() => {
    if (status !== 'incoming') {
      if (incomingTimerRef.current) clearInterval(incomingTimerRef.current);
      incomingTimerRef.current = null;
      setAutoDeclineSecondsLeft(INCOMING_AUTO_DECLINE_MS / 1000);
      return;
    }
    setAutoDeclineSecondsLeft(INCOMING_AUTO_DECLINE_MS / 1000);
    incomingTimerRef.current = setInterval(() => {
      setAutoDeclineSecondsLeft(prev => {
        if (prev <= 1) {
          // Decline + clear here is handled by the effect above when status
          // flips back to idle.
          if (incomingChallenge?.roomId) {
            rejectChallenge(incomingChallenge.roomId, incomingChallenge.battleType);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (incomingTimerRef.current) clearInterval(incomingTimerRef.current);
      incomingTimerRef.current = null;
    };
  }, [status, incomingChallenge, rejectChallenge]);

  // ── Error auto-clear ─────────────────────────────────────────────────
  useEffect(() => {
    if (!errorMessage) return;
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(() => {
      clearError();
    }, ERROR_AUTO_CLEAR_MS);
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, [errorMessage, clearError]);

  // ── Creator auto-starts the game once the opponent has joined ────────
  // The server keeps the room in `waiting` until the creator explicitly
  // sends `fun_room_start`. We fire it as soon as both players are in the
  // room, so the opponent's accept directly drives the game forward
  // instead of leaving everyone stuck in the lobby.
  useEffect(() => {
    if (status !== 'challenging') return;
    const roomId = pendingChallenge?.roomId;
    const battleType = pendingChallenge?.battleType;
    if (!roomId || !battleType) return;
    // Critical gate: the session store may still hold a room mirror from a
    // prior battle where THIS user was the invitee (e.g. they declined or
    // the previous session ended). Without this match, the auto-start can
    // fire instantly off stale `players` data — server replies
    // NOT_ENOUGH_PLAYERS and the real 30 s invite timer eventually expires.
    if (roomMirrorId !== roomId) return;
    if (roomState && roomState !== 'waiting') return;
    if (roomPlayersCount < 2) return;
    if (startedRoomRef.current === roomId) return;
    startedRoomRef.current = roomId;
    startGame(roomId, battleType);
  }, [
    status,
    roomMirrorId,
    roomPlayersCount,
    roomState,
    pendingChallenge,
    startGame,
  ]);

  // Reset the auto-start guard between battles.
  useEffect(() => {
    if (status === 'idle' || status === 'ended') {
      startedRoomRef.current = null;
    }
  }, [status]);

  // ── Auto-navigate when a session starts ──────────────────────────────
  useEffect(() => {
    if (status === 'in_battle' && session?.battleId) {
      try {
        navigation.navigate('FunChallengeBattle');
      } catch {
        // Navigation may not be ready in some auth states — non-fatal.
      }
    }
  }, [status, session?.battleId, navigation]);

  // ── Puzzle Royale: auto-navigate to its battle screen ───────────────────
  // The Multiplayer tab in the FunChallenges lobby is options-ONLY. The
  // PuzzleRoyaleBattle screen hosts BOTH the waiting room (status 'lobby')
  // and the live race (status 'in_battle'). So navigate as soon as a lobby
  // exists (host on create, joiner on join, link-opener on join) — everyone
  // converges on the battle screen, which shows the waiting room until start.
  const royaleStatus = useFunChallengesRoyaleStore(s => s.status);
  const royaleRoomId = useFunChallengesRoyaleStore(
    s => s.lobby?.roomId ?? s.session?.roomId ?? null,
  );
  useEffect(() => {
    // Never yank a user off the classroom page into the royale battle within
    // the same tab — the classroom flow opens the royale in a NEW tab. (Mirrors
    // the 1v1 listener's /classroom guard.)
    const onClassroom =
      typeof window !== 'undefined' &&
      typeof window.location?.pathname === 'string' &&
      window.location.pathname.toLowerCase().includes('/classroom');
    if (onClassroom) {
      return;
    }
    if (
      (royaleStatus === 'lobby' || royaleStatus === 'in_battle') &&
      royaleRoomId
    ) {
      try {
        navigation.navigate('PuzzleRoyaleBattle');
      } catch {
        // non-fatal
      }
    }
  }, [royaleStatus, royaleRoomId, navigation]);

  // A royale error that clears the lobby (e.g. joining a code AFTER the race
  // started → ROOM_NOT_WAITING, which reset()s the store) leaves the user on
  // the now-idle battle route. Bounce them to the lobby so they see the error
  // banner there and can pick another option.
  const royaleError = useFunChallengesRoyaleStore(s => s.errorMessage);
  useEffect(() => {
    const onClassroom =
      typeof window !== 'undefined' &&
      typeof window.location?.pathname === 'string' &&
      window.location.pathname.toLowerCase().includes('/classroom');
    if (onClassroom) {
      return;
    }
    if (royaleError && royaleStatus === 'idle') {
      try {
        navigation.navigate('FunChallengesLobby', {mode: 'multiplayer'});
      } catch {
        // non-fatal
      }
    }
  }, [royaleError, royaleStatus, navigation]);

  // ── Item 6: bring the user back to BattleScreen when the session ends
  //    while they're off-route. The BattleScreen renders the result modal
  //    as soon as it mounts in `status === 'ended'`. We avoid yanking the
  //    user out of /classroom (per item 4).
  const endedBattleId = useFunChallengesBattleStore(
    s => s.endResult?.battleId ?? null,
  );
  useEffect(() => {
    if (status !== 'ended' || !endedBattleId) return;
    const onClassroom =
      typeof window !== 'undefined' &&
      typeof window.location?.pathname === 'string' &&
      window.location.pathname.toLowerCase().includes('/classroom');
    if (onClassroom) return;
    try {
      navigation.navigate('FunChallengeBattle');
    } catch {
      // non-fatal
    }
  }, [status, endedBattleId, navigation]);

  // ── Item 5: "Challenge in Progress" pill lives in the app header
  //    (see src/Atoms/Header/index.tsx → FunChallengeInProgressPill). The
  //    listener doesn't render it — kept here for breadcrumbs.
  // ── Item 10: DND auto-decline. When `allowChallenge` is false (Do Not
  //    Disturb is on, set by the user via Header/Settings), incoming
  //    Fun Challenge invites are silently rejected so they never reach
  //    the listener modal. The challenger sees the normal ROOM_CLOSED
  //    decline path. Same path also handles:
  //      - 2026-05-20 bug 1: classroom suppression. We were already hiding
  //        the modal on /classroom, but the pending-invite Redis key stayed
  //        set until its 60s TTL, blocking subsequent invites with the
  //        misleading "Opponent already has a pending challenge" error.
  //        Auto-declining clears the key immediately.
  //      - 2026-05-20 bug 2: result screen. An invite that arrives while
  //        the user is still on `status === 'ended'` (looking at the
  //        result modal) wipes endResult via setIncomingChallenge → the
  //        result screen disappears. Auto-decline preserves the result
  //        view AND clears the server-side pending-invite key so the
  //        next challenger doesn't get the misleading "pending" error.
  const allowChallenge = useGamePlayState(s => s.allowChallenge);
  // Auto-decline incoming invites while the user is on /classroom (bug 1
  // — without this the pending-invite Redis key sits set until its 60s
  // TTL, blocking future challengers with "Opponent already has a pending
  // challenge"). The result-screen case (bug 2) is handled earlier in
  // the socket layer (useFunChallengesSocket.ts `fun_room_invite`), where
  // invites arriving while `status === 'ended'` are silently rejected
  // without mutating the store — so the result modal stays put.
  const isOnClassroomForDecline =
    typeof window !== 'undefined' &&
    typeof window.location?.pathname === 'string' &&
    window.location.pathname.toLowerCase().includes('/classroom');
  useEffect(() => {
    const shouldAutoDecline = !allowChallenge || isOnClassroomForDecline;
    if (!shouldAutoDecline) return;
    if (status !== 'incoming' || !incomingChallenge?.roomId) return;
    rejectChallenge(incomingChallenge.roomId, incomingChallenge.battleType);
  }, [
    allowChallenge,
    isOnClassroomForDecline,
    status,
    incomingChallenge?.roomId,
    incomingChallenge?.battleType,
    rejectChallenge,
  ]);

  // ── Render ───────────────────────────────────────────────────────────
  // Item 4 (bug sweep): suppress the listener modal when the user is in
  // /classroom. Mirrors the convention used by Atoms/Header,
  // Atoms/MainMenu, and Atoms/Header/Components/Dropdown which all gate
  // on `window.location.pathname.toLowerCase().includes('/classroom')`.
  // The incoming challenge still flows into the store, so a manual
  // navigation back to the lobby surfaces it then; the classroom session
  // is just protected from interruption.
  const isOnClassroom =
    typeof window !== 'undefined' &&
    typeof window.location?.pathname === 'string' &&
    window.location.pathname.toLowerCase().includes('/classroom');

  // 2026-05-20 UI-2: errors get their own visual modal (ChallengeRejected
  // — the same Rive-animated component PlayChess uses for "Opponent
  // unavailable" / "Tournament starting soon"). Splitting it out of
  // SlideModal so the look matches the PlayChess pattern.
  const errorVisible = !isOnClassroom && !!errorMessage;
  const visible = useMemo(
    () =>
      !isOnClassroom &&
      (status === 'challenging' || status === 'incoming'),
    [status, isOnClassroom],
  );

  const onClose = () => {
    if (status === 'challenging') {
      cancelChallenge();
    } else if (status === 'incoming' && incomingChallenge?.roomId) {
      rejectChallenge(
        incomingChallenge.roomId,
        incomingChallenge.battleType,
      );
    } else if (errorMessage) {
      clearError();
    }
  };

  if (!visible && !errorVisible) return null;

  return (
    <>
      {visible && (
        <SlideModal
          visible={visible}
          onClose={onClose}
          modalHeaderTitle={
            status === 'incoming'
              ? 'Incoming challenge'
              : 'Waiting for opponent…'
          }
          type="center"
          contentPadding={20}>
          <View style={styles.body}>
            {status === 'challenging' && (
              <ChallengingBody
                opponentName={pendingChallenge?.opponentName}
                onCancel={cancelChallenge}
              />
            )}

            {status === 'incoming' && incomingChallenge && (
              <IncomingBody
                challengerName={incomingChallenge.challengerName}
                battleType={incomingChallenge.battleType}
                timerSec={incomingChallenge.settings.timerSec}
                orientation={incomingChallenge.settings.orientation as string}
                maxWrong={
                  typeof incomingChallenge.settings.maxWrong === 'number'
                    ? (incomingChallenge.settings.maxWrong as number)
                    : undefined
                }
                targetCorrect={
                  typeof incomingChallenge.settings.targetCorrect === 'number'
                    ? (incomingChallenge.settings.targetCorrect as number)
                    : undefined
                }
                secondsLeft={autoDeclineSecondsLeft}
                onAccept={() =>
                  acceptChallenge(
                    incomingChallenge.roomId,
                    incomingChallenge.battleType,
                  )
                }
                onDecline={() =>
                  rejectChallenge(
                    incomingChallenge.roomId,
                    incomingChallenge.battleType,
                  )
                }
              />
            )}
          </View>
        </SlideModal>
      )}

      {/* 2026-05-20 UI-2: surface FunChallenge server errors via the same
          Rive-animated modal PlayChess uses (ChallengeRejected). Most
          errors in this stream come from cc-game-service's
          checkPlayerNotBusy — "Opponent is currently in a chess game",
          "Opponent already has a pending challenge", "Your tournament
          starts in N minute(s)" — and they read much more cleanly with
          the same visual treatment as PlayChess. */}
      <ChallengeRejected
        Visible={errorVisible}
        message={errorMessage || ''}
        handle_close={clearError}
      />
    </>
  );
}

function ChallengingBody({
  opponentName,
  onCancel,
}: {
  opponentName: string | undefined;
  onCancel: () => void;
}) {
  return (
    <Box>
      <CCText style={styles.bodyText}>
        {opponentName
          ? `Waiting for ${opponentName} to accept your challenge…`
          : 'Waiting for your opponent to accept…'}
      </CCText>
      <CCText style={styles.hintText}>
        They have 30 seconds to respond before the room expires.
      </CCText>
      <View style={styles.buttonRow}>
        <CCButton onPress={onCancel}>Cancel challenge</CCButton>
      </View>
    </Box>
  );
}

function IncomingBody({
  challengerName,
  battleType,
  timerSec,
  orientation,
  maxWrong,
  targetCorrect,
  secondsLeft,
  onAccept,
  onDecline,
}: {
  challengerName: string;
  battleType: BattleType;
  timerSec: number;
  orientation: string;
  maxWrong?: number;
  targetCorrect?: number;
  secondsLeft: number;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const isPTB = battleType === BattleType.POSITION_TRAINER;
  const isPDB = battleType === BattleType.PUZZLE_DASH;
  // Match the labels used by the lobby tiles (BattleLobby.tsx) so the
  // invite copy lines up with what the challenger picked.
  const matchLabel = isPDB
    ? 'Puzzle Dash'
    : isPTB
    ? 'Puzzle Duel'
    : 'Square Trainer Challenge';
  // PDB has no timer + orientation in the way ST/PTB do — it's race-to-N
  // with a mistake cap. Surface those instead.
  const pdbMistakeText =
    (maxWrong ?? 5) >= 100
      ? 'Unlimited mistakes'
      : `${maxWrong ?? 5} mistake${(maxWrong ?? 5) === 1 ? '' : 's'} allowed`;
  const hintText = isPDB
    ? `Race to ${targetCorrect ?? 15} · ${pdbMistakeText}`
    : isPTB
    ? `${timerSec}s · ${maxWrong ?? 5} mistake${(maxWrong ?? 5) === 1 ? '' : 's'} allowed`
    : `${timerSec}s · Play as ${orientation === 'black' ? 'Black' : 'White'}`;
  return (
    <Box>
      <CCText style={styles.bodyText}>
        {challengerName} wants to challenge you to a {matchLabel}.
      </CCText>
      <CCText style={styles.hintText}>{hintText}</CCText>
      <CCText style={styles.countdownText}>
        Auto-decline in {secondsLeft}s
      </CCText>
      <View style={styles.buttonRowSplit}>
        <Pressable onPress={onDecline} style={[styles.btn, styles.btnSecondary]}>
          <CCText style={styles.btnTextSecondary}>Decline</CCText>
        </Pressable>
        <Pressable onPress={onAccept} style={[styles.btn, styles.btnPrimary]}>
          <CCText style={styles.btnTextPrimary}>Accept</CCText>
        </Pressable>
      </View>
    </Box>
  );
}

// ErrorBody removed 2026-05-20 (UI-2): server errors now render via
// ChallengeRejected at the top level (same Rive modal PlayChess uses).

const styles = StyleSheet.create({
  body: {paddingVertical: vs(8)},
  bodyText: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '600',
    marginBottom: vs(8),
  },
  hintText: {
    fontSize: FontSizes[14] || 14,
    color: CCColors.GreyDarkest ?? '#666',
    marginBottom: vs(12),
  },
  countdownText: {
    fontSize: FontSizes[14] || 14,
    color: CCColors.Red ?? '#C0392B',
    fontWeight: '600',
    marginBottom: vs(16),
  },
  buttonRow: {marginTop: vs(8)},
  buttonRowSplit: {flexDirection: 'row', gap: 12, marginTop: vs(8)},
  btn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  btnPrimary: {
    backgroundColor: CCColors.Primary?.Brown ?? '#4D3F37',
  },
  btnSecondary: {
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: CCColors.White,
  },
  btnTextPrimary: {color: CCColors.White, fontWeight: '600'},
  btnTextSecondary: {color: CCColors.GreyDarkest ?? '#444', fontWeight: '600'},
});
